// Ultimate Card Game (UCG) - card-art thumbnail URLs.
//
// Card-sized views (hand, deck builder, pack reveal, Arsenal thumbs) show the art at <= ~175 css px
// wide, but assets/cards/*.png decode to up to 16 MB each. getCardThumbUrl() points them at the 448 px
// WebP baked by scripts/bake-card-thumbs.mjs instead. Icon-sized views (the Supply reels, 56-88 px) ask
// for size 's': a 224 px WebP, still >= 2.5x their css size on a 3x phone, at a quarter of the memory. Full-size views (pack inspect, Arsenal
// preview, avatars) keep using getCardImageUrl() from cards.js.
//
// Lives outside cards.js on purpose: that file is rewritten by the admin tooling
// (js/admin/cardsWriter.mjs), this one is not.
import { ASSET_VERSION, CARDS_DATA, getActiveCards, getGoldCardImage } from './cards.js';
import { CARD_THUMBS } from './cardThumbs.js';

// Global cache of pre-decoded thumbnail images.
// Holding references in memory prevents the browser from evicting decoded GPU textures,
// guaranteeing zero pop-in and instant visibility during fast scrolling in the deckbuilder.
export const PRELOADED_CARD_THUMBS = new Map();
let _isPreloading = false;
let _hasPreloaded = false;
let _preloadGeneration = 0;

function isMobileClient() {
  if (typeof window === 'undefined') return false;
  const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '';
  const coarse = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  const touchPoints = typeof navigator !== 'undefined' && (navigator.maxTouchPoints || 0) > 0;
  return /Mobi|Android|iPhone|iPad|iPod/i.test(ua) || coarse || touchPoints || (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 900px)').matches);
}

// size: 'm' (448 px, card-sized views, default) | 's' (224 px, icon-sized views).
export function getCardThumbUrl(imageName, size = 'm') {
  const file = imageName || 'Marine_1.png';
  // Art added without re-baking simply keeps the full-size image; nothing 404s.
  if (!CARD_THUMBS.has(file)) return `assets/cards/${file}?v=${ASSET_VERSION}`;
  const dir = size === 's' ? 'thumb-s' : 'thumb';
  return `assets/cards/${dir}/${file.replace(/\.[^.]+$/, '')}.webp?v=${ASSET_VERSION}`;
}

/**
 * Preloads and decodes card thumbnail images into browser memory.
 * Decodes all active card thumbnails upfront so they are immediately visible on scroll.
 * On mobile devices, uses idle-staggered batching to prevent CPU/RAM thread saturation on boot.
 */
export function preloadAllCardThumbnails(cards = null) {
  if (typeof Image === 'undefined') return;
  if (_hasPreloaded || _isPreloading) return;
  _isPreloading = true;
  const currentGen = ++_preloadGeneration;

  const targetCards = cards || (typeof getActiveCards === 'function' ? getActiveCards() : CARDS_DATA);
  if (!Array.isArray(targetCards)) {
    _isPreloading = false;
    return;
  }

  // Sort cards by cost, then name so preloading strictly follows the visual scroll order of the deckbuilder
  const sortedCards = [...targetCards].sort((a, b) => (a.cost || 0) - (b.cost || 0) || (a.name || '').localeCompare(b.name || ''));

  const isMobile = isMobileClient();

  if (!isMobile) {
    // Desktop: Fast upfront decode following visual scroll order
    for (let i = 0; i < sortedCards.length; i++) {
      if (currentGen !== _preloadGeneration) return;
      const card = sortedCards[i];
      const goldImg = getGoldCardImage(card?.name);
      const files = [card?.images?.[0] || 'Marine_1.png'];
      if (goldImg) files.push(goldImg);

      for (const file of files) {
        const url = getCardThumbUrl(file, 'm');
        if (url && !PRELOADED_CARD_THUMBS.has(url)) {
          const img = new Image();
          img.loading = 'eager';
          if (i < 12 && 'fetchPriority' in img) {
            img.fetchPriority = 'high';
          }
          img.decoding = 'async';
          img.src = url;
          PRELOADED_CARD_THUMBS.set(url, img);
          if (typeof img.decode === 'function') {
            img.decode().catch(() => {});
          }
        }
      }
    }
    if (currentGen === _preloadGeneration) {
      _isPreloading = false;
      _hasPreloaded = true;
    }
    return;
  }

  // Mobile: Staggered chunks (6 cards/tick) to keep frame pacing smooth and avoid thermal spike
  let idx = 0;
  const CHUNK_SIZE = 6;
  const processChunk = () => {
    if (currentGen !== _preloadGeneration) return;
    const end = Math.min(idx + CHUNK_SIZE, sortedCards.length);
    for (; idx < end; idx++) {
      const card = sortedCards[idx];
      const goldImg = getGoldCardImage(card?.name);
      const files = [card?.images?.[0] || 'Marine_1.png'];
      if (goldImg) files.push(goldImg);

      for (const file of files) {
        const url = getCardThumbUrl(file, 'm');
        if (url && !PRELOADED_CARD_THUMBS.has(url)) {
          const img = new Image();
          img.loading = 'eager';
          img.decoding = 'async';
          img.src = url;
          PRELOADED_CARD_THUMBS.set(url, img);
          if (typeof img.decode === 'function') {
            img.decode().catch(() => {});
          }
        }
      }
    }
    if (idx < sortedCards.length) {
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(() => processChunk(), { timeout: 150 });
      } else {
        setTimeout(processChunk, 60);
      }
    } else {
      _isPreloading = false;
      _hasPreloaded = true;
    }
  };

  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => processChunk(), { timeout: 200 });
  } else {
    setTimeout(processChunk, 100);
  }
}

/**
 * Releases preloaded card thumbnail images from memory.
 * Cancels any in-flight staggered preloading chunks and clears the global map.
 */
export function releasePreloadedThumbnails() {
  _preloadGeneration++;
  _isPreloading = false;
  _hasPreloaded = false;
  for (const img of PRELOADED_CARD_THUMBS.values()) {
    try {
      img.onload = null;
      img.onerror = null;
      img.src = '';
    } catch {}
  }
  PRELOADED_CARD_THUMBS.clear();
}

// Preloading is orchestrated explicitly (e.g. from startScreen after initial render)
// to avoid blocking the critical path and First Contentful Paint upon module load.
